import { useFeatureFlagEnabled } from 'posthog-js/react'
import { createContext, useContext, useEffect, useRef } from 'react'
import { isTourSeen, TOUR_REGISTRY } from '@/lib/tours/registry'

interface TourContextValue {
  startTour: (tourId: string) => void
  endTour: () => void
}

export const TourContext = createContext<TourContextValue | undefined>(undefined)

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext)
  if (!ctx) {
    throw new Error('useTour must be used within TourProvider')
  }
  return ctx
}

/**
 * Opts the calling page into auto-triggering a tour on mount. Fires once when:
 *   1. The tour's PostHog feature flag is enabled, AND
 *   2. `localStorage[tour-<id>-seen]` is not set (per-device dismissal state), AND
 *   3. `enabled` is true (defaults true; pass a readiness flag for tours whose
 *      anchors are gated on async state like loading, entitlement, or
 *      conditional empty-state rendering).
 *
 * The `enabled` parameter exists so a page can call this hook unconditionally
 * (React requires it) but defer firing until its anchors actually exist —
 * without that, a tour could start against a skeleton / "not entitled" / error
 * state, the user dismisses welcome, and the seen-key locks them out forever.
 *
 * No-op for manual-trigger tours. Safe to call from a useEffect-eligible scope.
 *
 * Dev-only escape hatch: when `import.meta.env.DEV` is true OR the build is a
 * Vercel preview (`VERCEL_ENV === 'preview'`), setting
 * `localStorage[tour-debug-force]` or `localStorage[tour-<id>-debug-force]` to
 * `'true'` bypasses the feature-flag check. Production builds ignore both
 * keys. The preview-build branch exists so the DevToursSwitcher panel — which
 * renders under the same condition — can actually trigger tours on the
 * deployed dev environment.
 */
export function useAutoTour(tourId: string, enabled: boolean = true): void {
  const { startTour, endTour } = useTour()
  const definition = TOUR_REGISTRY[tourId]
  const flagEnabled = useFeatureFlagEnabled(definition?.featureFlag ?? '')
  const startedRef = useRef(false)

  useEffect(() => {
    let debugForce = false
    if (import.meta.env.DEV || import.meta.env.VERCEL_ENV === 'preview') {
      try {
        debugForce
          = localStorage.getItem('tour-debug-force') === 'true'
            || localStorage.getItem(`tour-${tourId}-debug-force`) === 'true'
      }
      catch {
        // localStorage unavailable — fall back to flag check below.
      }
    }
    if (!enabled || !definition || definition.trigger !== 'auto' || (!flagEnabled && !debugForce) || startedRef.current) {
      return
    }
    if (isTourSeen(definition)) {
      return
    }
    startedRef.current = true
    startTour(tourId)

    // Shepherd appends step popovers + overlay to document.body, outside
    // React's tree. If the caller unmounts mid-tour (page navigation), the
    // target selectors disappear and the popovers float with no anchor.
    // Tear down the active tour when the calling page unmounts. Reset the
    // started flag so StrictMode's simulated unmount→remount can re-arm the
    // tour on the second mount instead of leaving it permanently cancelled.
    return () => {
      if (startedRef.current) {
        endTour()
        startedRef.current = false
      }
    }
  }, [enabled, definition, flagEnabled, tourId, startTour, endTour])
}
